import { useState, useMemo } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDateTime } from "@/lib/demo-data";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import {
  Download,
  Search,
  ArrowUpRight,
  ArrowDownRight,
  RotateCcw,
  CreditCard,
  Gift,
  Minus,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

type TransactionType =
  | "PURCHASE"
  | "PENDING"
  | "UNLOCK"
  | "REFUND"
  | "BONUS_WELCOME"
  | "BONUS_REFERRAL"
  | "MANUAL_ADD"
  | "MANUAL_REMOVE";

interface Transaction {
  id: string;
  companyId: string;
  companyName: string;
  type: TransactionType;
  amount: number;
  balance: number;
  label: string;
  refId: string | null;
  invoiceUrl: string | null;
  createdAt: string;
}

interface TransactionsResponse {
  items: Transaction[];
  total: number;
  summary?: {
    purchases: number;
    unlocks: number;
    refunds: number;
    bonuses: number;
  };
}

type TypeFilter = "ALL" | TransactionType;

const TYPE_LABELS: Record<TransactionType, string> = {
  PURCHASE: "Achat",
  PENDING: "En attente",
  UNLOCK: "Déblocage",
  REFUND: "Remboursement",
  BONUS_WELCOME: "Bonus bienvenue",
  BONUS_REFERRAL: "Bonus parrainage",
  MANUAL_ADD: "Ajout manuel",
  MANUAL_REMOVE: "Retrait manuel",
};

const TYPE_VARIANTS: Record<TransactionType, "default" | "secondary" | "destructive" | "outline"> = {
  PURCHASE: "default",
  PENDING: "outline",
  UNLOCK: "secondary",
  REFUND: "destructive",
  BONUS_WELCOME: "default",
  BONUS_REFERRAL: "default",
  MANUAL_ADD: "secondary",
  MANUAL_REMOVE: "destructive",
};

function TypeIcon({ type }: { type: TransactionType }) {
  switch (type) {
    case "PURCHASE":
      return <CreditCard className="h-4 w-4 text-success" />;
    case "UNLOCK":
      return <ArrowDownRight className="h-4 w-4 text-warning" />;
    case "REFUND":
      return <RotateCcw className="h-4 w-4 text-destructive" />;
    case "BONUS_WELCOME":
    case "BONUS_REFERRAL":
      return <Gift className="h-4 w-4 text-primary" />;
    case "MANUAL_ADD":
      return <ArrowUpRight className="h-4 w-4 text-success" />;
    case "MANUAL_REMOVE":
      return <Minus className="h-4 w-4 text-destructive" />;
    default:
      return <DollarSign className="h-4 w-4 text-muted-foreground" />;
  }
}

function downloadCsv(transactions: Transaction[]) {
  const headers = ["Date", "Entreprise", "Type", "Montant", "Solde", "Libellé", "Réf"];
  const rows = transactions.map((t) => [
    formatDateTime(t.createdAt),
    t.companyName,
    TYPE_LABELS[t.type] || t.type,
    t.amount.toString(),
    t.balance.toString(),
    t.label,
    t.refId || "",
  ]);
  const csvContent = [headers, ...rows]
    .map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success("Export CSV téléchargé");
}

export default function Transactions() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const limit = 50;

  const { data, isLoading } = trpc.admin.travauxbtp.listAllTransactions.useQuery({
    page,
    limit,
    search: search || undefined,
    type: typeFilter === "ALL" ? undefined : typeFilter,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const transactions: Transaction[] = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit) || 1;

  const summaryStats = useMemo(() => {
    if (data?.summary) return data.summary;
    const purchases = transactions
      .filter((t) => t.type === "PURCHASE" || t.type === "MANUAL_ADD")
      .reduce((s, t) => s + Math.abs(t.amount), 0);
    const unlocks = transactions
      .filter((t) => t.type === "UNLOCK")
      .reduce((s, t) => s + Math.abs(t.amount), 0);
    const refunds = transactions
      .filter((t) => t.type === "REFUND")
      .reduce((s, t) => s + Math.abs(t.amount), 0);
    const bonuses = transactions
      .filter((t) => t.type === "BONUS_WELCOME" || t.type === "BONUS_REFERRAL")
      .reduce((s, t) => s + Math.abs(t.amount), 0);
    return { purchases, unlocks, refunds, bonuses };
  }, [data, transactions]);

  if (isLoading) {
    return (
      <AdminLayout>
        <ListPageSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold">Transactions crédits</h1>
            <p className="text-muted-foreground">
              Vue globale de toutes les transactions crédits TravauxBTP
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() =>
              transactions.length > 0
                ? downloadCsv(transactions)
                : toast.error("Aucune donnée à exporter")
            }
            disabled={transactions.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Achats crédits</CardTitle>
              <CreditCard className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">
                +{summaryStats.purchases}
              </div>
              <p className="text-xs text-muted-foreground">crédits achetés sur la période</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Déblocages</CardTitle>
              <ArrowDownRight className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-warning">
                -{Math.abs(summaryStats.unlocks)}
              </div>
              <p className="text-xs text-muted-foreground">crédits consommés</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Remboursements</CardTitle>
              <RotateCcw className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">
                -{Math.abs(summaryStats.refunds)}
              </div>
              <p className="text-xs text-muted-foreground">crédits remboursés</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Bonus</CardTitle>
              <Gift className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">
                +{summaryStats.bonuses}
              </div>
              <p className="text-xs text-muted-foreground">
                crédits offerts (bienvenue + parrainage)
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par entreprise ou libellé..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9"
                />
              </div>
              <Select
                value={typeFilter}
                onValueChange={(v) => {
                  setTypeFilter(v as TypeFilter);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Tous les types</SelectItem>
                  <SelectItem value="PURCHASE">Achat</SelectItem>
                  <SelectItem value="UNLOCK">Déblocage</SelectItem>
                  <SelectItem value="REFUND">Remboursement</SelectItem>
                  <SelectItem value="BONUS_WELCOME">Bonus bienvenue</SelectItem>
                  <SelectItem value="BONUS_REFERRAL">Bonus parrainage</SelectItem>
                  <SelectItem value="MANUAL_ADD">Ajout manuel</SelectItem>
                  <SelectItem value="MANUAL_REMOVE">Retrait manuel</SelectItem>
                  <SelectItem value="PENDING">En attente</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className="w-[160px]"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                className="w-[160px]"
              />
              {(search || typeFilter !== "ALL" || dateFrom || dateTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearch("");
                    setTypeFilter("ALL");
                    setDateFrom("");
                    setDateTo("");
                    setPage(1);
                  }}
                >
                  Réinitialiser
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Transactions table */}
        <Card>
          <CardContent className="p-0">
            {transactions.length === 0 ? (
              <EmptyState
                title="Aucune transaction"
                description="Aucune transaction ne correspond aux filtres sélectionnés."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Entreprise</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead className="text-right">Solde après</TableHead>
                    <TableHead>Libellé</TableHead>
                    <TableHead>Réf</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDateTime(tx.createdAt)}
                      </TableCell>
                      <TableCell className="font-medium max-w-[200px] truncate">
                        {tx.companyName}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TypeIcon type={tx.type} />
                          <Badge variant={TYPE_VARIANTS[tx.type]}>
                            {TYPE_LABELS[tx.type]}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        <span
                          className={tx.amount >= 0 ? "text-success" : "text-destructive"}
                        >
                          {tx.amount >= 0 ? "+" : ""}
                          {tx.amount}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {tx.balance}
                      </TableCell>
                      <TableCell className="max-w-[250px] truncate text-sm text-muted-foreground">
                        {tx.label}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {tx.refId ? (
                          <span className="font-mono text-xs">
                            {tx.refId.slice(0, 8)}...
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page} sur {totalPages} — {total} transactions
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
